import React, { createContext, useContext, useState, useRef, useCallback, useEffect } from 'react';
import { useRecents } from '../hooks/useRecents';
import { useTracking } from '../hooks/useTracking';
import { usePlaybackCoordinator } from './PlaybackCoordinatorContext';

import { API_URL } from '../utils/api';

const PlayerContext = createContext(null);

export const usePlayer = () => {
  const context = useContext(PlayerContext);
  if (!context) {
    throw new Error('usePlayer must be used within a PlayerProvider');
  }
  return context;
};

export const PlayerProvider = ({ children }) => {
  const [currentLoop, setCurrentLoop] = useState(null);
  const [isPlaying, setIsPlaying] = useState(false);
  const [progress, setProgress] = useState(0);
  const [duration, setDuration] = useState(0);
  const audioRef = useRef(new Audio());
  const { recentLoops, addToRecents, clearRecents } = useRecents();
  const { trackPreview, trackDownload, trackVibeClick } = useTracking();
  const { requestOwnership, releaseOwnership, registerStopFn } = usePlaybackCoordinator();
  
  // Handle audio events
  useEffect(() => {
    const audio = audioRef.current;
    
    const handleTimeUpdate = () => {
      if (audio.duration) {
        setProgress((audio.currentTime / audio.duration) * 100);
      }
    };
    
    const handleLoadedMetadata = () => {
      setDuration(audio.duration);
    };
    
    const handleEnded = () => {
      setIsPlaying(false);
      setProgress(0);
      // Loop playback
      audio.currentTime = 0;
      audio.play();
      setIsPlaying(true);
    };
    
    const handleError = (e) => {
      console.error('Audio error:', e);
      setIsPlaying(false);
    };
    
    audio.addEventListener('timeupdate', handleTimeUpdate);
    audio.addEventListener('loadedmetadata', handleLoadedMetadata);
    audio.addEventListener('ended', handleEnded);
    audio.addEventListener('error', handleError);
    
    return () => {
      audio.removeEventListener('timeupdate', handleTimeUpdate);
      audio.removeEventListener('loadedmetadata', handleLoadedMetadata);
      audio.removeEventListener('ended', handleEnded);
      audio.removeEventListener('error', handleError);
    };
  }, []);
  
  // Stop function — also used by the coordinator to halt card playback
  const stop = useCallback(() => {
    const audio = audioRef.current;
    audio.pause();
    audio.currentTime = 0;
    setIsPlaying(false);
    setProgress(0);
    setCurrentLoop(null);
    releaseOwnership('card');
  }, [releaseOwnership]);
  
  // Register stop function with coordinator
  useEffect(() => {
    registerStopFn('card', stop);
  }, [registerStopFn, stop]);
  
  const playLoop = useCallback((loop) => {
    const audio = audioRef.current;
    
    // If same loop, toggle play/pause
    if (currentLoop?.loop_id === loop.loop_id) {
      if (isPlaying) {
        audio.pause();
        setIsPlaying(false);
        releaseOwnership('card');
      } else {
        requestOwnership('card');
        audio.play();
        setIsPlaying(true);
      }
      return;
    }
    
    // Request ownership — stops preview if it's playing
    requestOwnership('card');
    
    // Play new loop - prepend API URL if needed
    audio.pause();
    const previewUrl = loop.preview_url.startsWith('http') 
      ? loop.preview_url 
      : `${API_URL}${loop.preview_url}`;
    audio.src = previewUrl;
    audio.load();
    audio.play()
      .then(() => {
        setCurrentLoop(loop);
        setIsPlaying(true);
        setProgress(0);
        // Add to recents
        addToRecents(loop);
        // Track preview play
        trackPreview(loop.loop_id);
      })
      .catch((err) => {
        console.error('Playback failed:', err);
        releaseOwnership('card');
      });
  }, [currentLoop, isPlaying, addToRecents, trackPreview, requestOwnership, releaseOwnership]);
  
  const pause = useCallback(() => {
    audioRef.current.pause();
    setIsPlaying(false);
    releaseOwnership('card');
  }, [releaseOwnership]);
  
  const resume = useCallback(() => {
    if (currentLoop) {
      requestOwnership('card');
      audioRef.current.play();
      setIsPlaying(true);
    }
  }, [currentLoop, requestOwnership]);
  
  const seek = useCallback((percent) => {
    const audio = audioRef.current;
    if (audio.duration) {
      audio.currentTime = (percent / 100) * audio.duration;
      setProgress(percent);
    }
  }, []);
  
  return (
    <PlayerContext.Provider value={{
      currentLoop,
      isPlaying,
      progress,
      duration,
      playLoop,
      pause,
      resume,
      seek,
      stop,
      recentLoops,
      clearRecents,
      trackDownload,
      trackVibeClick
    }}>
      {children}
    </PlayerContext.Provider>
  );
};
